import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import type { ScenarioDto, SynthesizeSpeechCommand } from '@/api-gen/api.schemas';
import { PersonaUsage } from '@/api-gen/api.schemas';
import { speechSynthesizeSpeech } from '@/api-gen/api.ts';

import AudioActions from '../../../actions/audio/audioAction.ts';
import type { AxiosResponse } from 'axios';
import * as DialogProcessorSelector from 'app/selectors/dialogProcessorSelector.ts';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';

import VoiceRecognitionStatus from 'app/models/voiceRecognition/VoiceRecognitionStatus.ts';
import { VoiceRecognitionActions } from 'app/actions/voiceRecognition/voiceRecognitionActions.ts';
import type { IDProcessState } from 'app/models/dialogs/dialogProcess.ts';
import { LinkedScenario } from 'app/models/intents/intentNodeProperties.ts';
import logger from 'app/utils/logger.ts';

function* getAudio() {
  const rootScenario: ScenarioDto = yield select(ScenarioSelectors.rootScenario);
  const dialogProcessorData: IDProcessState = yield select(DialogProcessorSelector.dialogProcessorSelector);

  try {
    const text: string = dialogProcessorData.blocks.currentBlock.text;

    if (!text) {
      throw new Error('No AI response for Text to Speech!');
    }

    const currentBlock = dialogProcessorData.blocks.currentBlock;

    // Prefer block persona voice; fall back to scenario default
    const blockPersona = currentBlock?.scenarioPersona?.persona;
    const defaultPersona = rootScenario?.scenarioPersonas?.find(
      sp => sp.personaUsages?.includes(PersonaUsage.Default)
    )?.persona;
    const persona = blockPersona ?? defaultPersona;

    const payload: SynthesizeSpeechCommand = {
      language: rootScenario.langCode,
      sex: persona?.gender,
      service: persona?.ttsProvider ?? rootScenario.ttsProvider,
      voiceName: persona?.voiceName,
      voiceId: persona?.voiceId,
      text: text,
      speed: rootScenario.speed,
      intentId: dialogProcessorData.blocks.currentBlock.id
    };

    const response: AxiosResponse<string> = yield call(
      // Orval client uses body object directly
      speechSynthesizeSpeech,
      payload as any
    );

    yield put(AudioActions.setAudioAIResponse(response.data));
    yield put(VoiceRecognitionActions.setRecognitionStatus(VoiceRecognitionStatus.AudioIsPlaying));
  } catch (ex) {
    logger.log(ex);
    yield put(VoiceRecognitionActions.setRecognitionStatus(VoiceRecognitionStatus.AudioWasEnded));
  }
}

function* watchGetAudio() {
  yield takeEvery(AudioActions.Type.GET_AUDIO, getAudio);
}

export default function* watchAllAudiosSaga() {
  yield fork(watchGetAudio);
}
